import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useAdminAuth } from '@/lib/admin/admin-context'
import { Shield, Eye, EyeOff } from 'lucide-react'

export default function AdminLoginPage() {
  const [key, setKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { isAuthenticated, login } = useAdminAuth()
  const navigate = useNavigate()

  // Already authenticated — redirect
  if (isAuthenticated) {
    navigate('/admin/dashboard', { replace: true })
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!key.trim()) return
    setError('')
    setLoading(true)
    try {
      await login(key.trim())
      navigate('/admin/dashboard')
    } catch {
      setError('Invalid admin key')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm p-8">
          {/* Header */}
          <div className="flex flex-col items-center gap-4 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-zinc-900 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div className="text-center">
              <h1 className="font-bold tracking-tight text-2xl" style={{ fontFamily: 'var(--font-syne)' }}>
                eT<span className="text-primary-500">X</span>
                <span className="text-zinc-300 mx-0.5 text-xl font-light">/</span>
                <span className="text-zinc-500 font-medium text-xl">Admin</span>
              </h1>
              <p className="text-sm text-zinc-400 mt-1">Internal tool — admin access only</p>
            </div>
          </div>

          {error && (
            <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600 mb-5">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-sm font-medium text-zinc-500 block mb-1.5">
                Admin Key
              </label>
              <div className="rounded-2xl border border-zinc-200 bg-zinc-50 transition-colors focus-within:border-zinc-400 focus-within:bg-white">
                <div className="relative">
                  <input
                    type={showKey ? 'text' : 'password'}
                    placeholder="Enter admin key"
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                    disabled={isLoading}
                    autoFocus
                    className="w-full bg-transparent text-sm text-zinc-900 placeholder:text-zinc-400 p-4 pr-12 rounded-2xl focus:outline-none disabled:opacity-50"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey(!showKey)}
                    className="absolute inset-y-0 right-3 flex items-center"
                  >
                    {showKey
                      ? <EyeOff className="w-5 h-5 text-zinc-400 hover:text-zinc-600 transition-colors" />
                      : <Eye className="w-5 h-5 text-zinc-400 hover:text-zinc-600 transition-colors" />}
                  </button>
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading || !key.trim()}
              className="w-full rounded-2xl bg-zinc-900 py-4 font-medium text-white hover:bg-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="size-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Verifying...
                </>
              ) : 'Access Admin Panel'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-zinc-400 hover:text-zinc-700 transition-colors"
            >
              Back to app login
            </button>
          </div>
        </div>

        <p className="text-[11px] text-zinc-300 text-center mt-4">
          Badders Holding Company LLC — Internal Use Only
        </p>
      </div>
    </div>
  )
}
